import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Spinner from '../components/spinner/Spinner';

// routes config
import privateRoutes from '../navigation/routes';
import { AccessNumberProvider } from '../services/AccessNumberProvider';
import { getUserInformation } from '../services/TokenHandler';
import {
  validatePermission,
  userPermissions,
  fillPermissions
} from '../utils/permissions/validatePermission';
import LoadingBarTop from '../components/barTop/barTop';

const AppContent = () => {
  const [permissionsReady, setPermissionsReady] = useState(false);
  const userInfo = getUserInformation();
  const {
    role: { name: nameRol }
  } = JSON.parse(userInfo);

  useEffect(() => {
    const fetchPermissions = async () => {
      await fillPermissions();
      setPermissionsReady(true);
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    if (privateRoutes.length === 0) {
      renderPrivateRoutes();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateRoutes]);

  const permission = (permissionType) => {
    const defaultPermission = +true;
    const permissionName = {
      VIPO: defaultPermission,
      WebRTC: defaultPermission,
      Payment: defaultPermission,
      Dashboard: defaultPermission,
      Hub: defaultPermission,
      'Access Numbers': +validatePermission(userPermissions('/access-number', 'get'), userInfo),
      FastLane: +validatePermission(userPermissions('/flane', 'get'), userInfo),
      CallPass: +validatePermission(userPermissions('/cpass', 'get'), userInfo),
      Vipp:
        +validatePermission(userPermissions('/vipp', 'get'), userInfo) ||
        +validatePermission(userPermissions('/vipo', 'get'), userInfo),
      Ventana: +validatePermission(userPermissions('/vnta', 'get'), userInfo),
      // WebRTC: +validatePermission(userPermissions('/webrtc', 'get'), userInfo),
      WhatsApp: +validatePermission(userPermissions('/whatsapp', 'get'), userInfo),
      Reports: +validatePermission(userPermissions(' /whatsapp-reports', 'get'), userInfo),
      Hub: +validatePermission(userPermissions('/whatsapp-dashboard', 'get'), userInfo),
      'Custom Links': defaultPermission,
      CDR: defaultPermission,

      Reports: defaultPermission,
      Hub: defaultPermission,

      Reports: +validatePermission(userPermissions('/reports/general', 'get'), userInfo),
      Products: +(
        validatePermission(userPermissions('/company/products', 'post'), userInfo) &&
        nameRol === 'SUPER-ADMIN'
      ),

      Accounts: +(nameRol === 'SUPER-ADMIN'), //TODO: Validate that only SUPER-ADMIN can access this
      'Accounts V2': 1,
      'Accounts new': +(nameRol === 'SUPER-ADMIN'),
      'Accounts details': 1,
      'Region Pricing': +(nameRol === 'SUPER-ADMIN'),
      Twilio: +(nameRol === 'SUPER-ADMIN'),
      DID: +(nameRol === 'SUPER-ADMIN'),
      'DID Manage': +validatePermission(userPermissions('/did-manage', 'get'), userInfo),
      Inventory: +validatePermission(userPermissions('/did-manage', 'get'), userInfo),
      'Buy (Conecta)': +(nameRol === 'SUPER-ADMIN'),
      'Region Pricing DID': +(nameRol === 'SUPER-ADMIN'),

      Hub: defaultPermission,
      'Custom Links': defaultPermission,
      Reports: defaultPermission,
      CDRs: +(nameRol === 'SUPER-ADMIN'),
      'Reports DID': +validatePermission(userPermissions('/did/call/reports', 'get'), userInfo),
      Inventory: +validatePermission(userPermissions('/did-manage', 'get'), userInfo),
      Users: +validatePermission(userPermissions('/user', 'get'), userInfo),
      Roles: +validatePermission(userPermissions('/roles', 'get'), userInfo),
      API: +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      VoIP: +validatePermission(userPermissions('/trunks', 'get'), userInfo),
      'IVR Configuration': +validatePermission(userPermissions('/node-assigns', 'get'), userInfo),
      Cronjobs: +(nameRol === 'SUPER-ADMIN'),
      'Config Jobs': +(nameRol === 'SUPER-ADMIN'),
      'Execution Jobs': +(nameRol === 'SUPER-ADMIN'),
      'Execution Logs': +(nameRol === 'SUPER-ADMIN'),
      'Provider Call Failures': +(nameRol === 'SUPER-ADMIN'),
      Support: defaultPermission,
      'Page Not Found': defaultPermission,
      Demos: +(nameRol === 'SUPER-ADMIN'),
      'AI Agent': +(nameRol === 'SUPER-ADMIN'),
      QuickPay: +(nameRol === 'SUPER-ADMIN'),
      'VIP - Outbound': +(nameRol === 'SUPER-ADMIN'),
      Vnta: +(nameRol === 'SUPER-ADMIN'),
      'Look-up':
        +(nameRol === 'SUPER-ADMIN') ||
        +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      Records:
        +(nameRol === 'SUPER-ADMIN') ||
        +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      'Look-up Records':
        +(nameRol === 'SUPER-ADMIN') ||
        +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      'Access Logs':
        +(nameRol === 'SUPER-ADMIN') ||
        +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      'Look-up Access Logs':
        +(nameRol === 'SUPER-ADMIN') ||
        +validatePermission(userPermissions('/api-connections', 'get'), userInfo),
      Irops: +(nameRol === 'SUPER-ADMIN'),
      'WhatsApp Onboarding': defaultPermission,
      // TODO: Remove default permissions
      'Invoicing Dashboard': defaultPermission,
      Invoices: defaultPermission,
      'Invoice Detail': defaultPermission
    };

    return permissionName[permissionType];
  };

  const addPermissions = privateRoutes.map((route) => ({
    ...route,
    permission: permission(route.name)
  }));

  const renderPrivateRoutes = () => {
    if (addPermissions) {
      return addPermissions
        .filter((route) => route.permission === 1)
        .map((route, idx) => {
          return (
            route.element && (
              <Route
                key={idx}
                path={route.path}
                exact={route.exact}
                name={route.name}
                element={<route.element />}
              />
            )
          );
        });
    }

    return <></>;
  };

  return (
    <AccessNumberProvider>
      <Suspense fallback={<LoadingBarTop />}>
        <Routes>
          {permissionsReady && privateRoutes && renderPrivateRoutes()}
          <Route path="/" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </Suspense>
    </AccessNumberProvider>
  );
};

export default React.memo(AppContent);
